import { FC, ReactNode, useId } from 'react';
import { IonPopover } from '@ionic/react';
import { createUseStyles } from 'react-jss';
import { BRAND, CHART_INK } from './agencyChartTheme';

const useStyles = createUseStyles({
    button: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 18,
        height: 18,
        flexShrink: 0,
        padding: 0,
        borderRadius: '50%',
        border: `1px solid ${BRAND.tealEdge}`,
        background: 'none',
        font: 'inherit',
        fontSize: 11,
        fontWeight: 700,
        lineHeight: 1,
        color: BRAND.deepTeal,
        cursor: 'pointer',

        '&:hover': {
            background: BRAND.tealWash,
            borderColor: BRAND.deepTeal
        }
    },

    popover: {
        '--max-width': '300px',
        '--width': '300px'
    },

    body: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 8,
        padding: '12px 14px',
        fontSize: 12.5,
        lineHeight: 1.55,
        color: CHART_INK.strongLabel
    },

    summary: {
        fontWeight: 500
    }
});

/**
 * What a panel shows and how to read it, tucked behind a mark beside its heading.
 *
 * Everyone reads it once and nobody reads it again, so leaving it printed under the heading costs
 * every later visit the space. Behind a control it is still one click away for the person who has
 * not met the panel before.
 *
 * A button rather than a hover target, so it works on a touch screen and can be reached by keyboard.
 */
export const InsightsInfoTip: FC<{
    label: string;
    /** Leads the explanation, set apart when there is more under it. */
    summary?: string;
    children?: ReactNode;
}> = ({ label, summary, children }) => {
    const classes = useStyles();
    const triggerId = `info-${useId()}`;

    return (
        <>
            <button
                id={triggerId}
                type="button"
                className={classes.button}
                data-id="insights-info-tip"
                aria-label={label}
            >
                ?
            </button>

            <IonPopover
                className={classes.popover}
                trigger={triggerId}
                triggerAction="click"
                side="bottom"
                alignment="end"
                showBackdrop={false}
                dismissOnSelect
            >
                <div className={classes.body}>
                    {summary && <div className={children ? classes.summary : undefined}>{summary}</div>}
                    {children && <div>{children}</div>}
                </div>
            </IonPopover>
        </>
    );
};
